import { ArrowLeft } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { MetricStrip } from '../components/MetricStrip'
import { mergePinduoduoProducts, usePinduoduoResource } from '../platform'
import { formatTimestamp, integer, money } from '../utils'
import { pinduoduoQueryWindowLabel } from '../pinduoduo'

const reportedMoney = (value: number | null) => value === null ? '—' : money(value)
const reportedInteger = (value: number | null) => value === null ? '—' : integer(value)
const reportedRatio = (value: number | null) => value === null ? '—' : value.toFixed(2)

export function PinduoduoProductDetailPage() {
  const { productId } = useParams<{ productId: string }>()
  const { data, error, loading, reload } = usePinduoduoResource()
  const product = useMemo(
    () => data ? mergePinduoduoProducts(data).find((item) => item.productId === productId) : undefined,
    [data, productId],
  )

  if (loading) return <div className="loading-state">正在加载拼多多商品详情…</div>
  if (error || !data) return <div className="error-state"><h1>拼多多商品数据加载失败</h1><p>{error}</p><button type="button" className="primary-button" onClick={() => void reload()}>重试</button></div>
  if (!product) return <div className="not-found"><h1>未找到该拼多多商品</h1><p>商品ID {productId || '未提供'}</p><Link to="/pinduoduo/products">返回商品列表</Link></div>

  const promotion = product.promotion
  const opportunity = product.opportunity
  const queryWindowLabel = pinduoduoQueryWindowLabel(data.meta.queryStart, data.meta.queryEnd)

  return (
    <div className="page-stack pdd-page">
      <div className="breadcrumb-row">
        <Link to="/pinduoduo/products"><ArrowLeft aria-hidden="true" />拼多多商品</Link>
        <span>/</span><span>{product.name}</span>
      </div>

      <div className="page-title-row">
        <div>
          <span className="pdd-platform-label">PINDUODUO PRODUCT</span>
          <h1>{product.name}</h1>
          <p>平台商品ID {product.productId} · {promotion && opportunity ? '推广与活动机会均覆盖' : promotion ? '推广商品' : '活动机会商品'}</p>
        </div>
        <div className="page-title-actions">
          <DataFreshnessBadge date={data.meta.queryEnd} label="推广数据截至" mode="on-demand" />
          <Link className="text-link" to="/pinduoduo">店铺总览</Link>
        </div>
      </div>

      <div className="pdd-caliber-note">
        <strong>规格未就绪：</strong>
        本页所有经营数据均是拼多多商品ID粒度，不是规格或SKU粒度。尚未取得稳定的官方规格ID，不会用商品名或京东SKU猜测映射。
      </div>

      <section className="detail-section">
        <div className="section-heading-row"><div><h2>{queryWindowLabel}推广商品汇总</h2><p>{data.meta.queryStart} 至 {data.meta.queryEnd} · 交易指标均为推广归因口径</p></div></div>
        {promotion ? <>
          <MetricStrip items={[
            { label: '推广花费', value: reportedMoney(promotion.spend), context: promotion.promotionName || '推广计划名未标注' },
            { label: '归因交易额', value: reportedMoney(promotion.attributedGmv), context: `归因ROI ${reportedRatio(promotion.attributedRoi)}` },
            { label: '净交易额', value: reportedMoney(promotion.netGmv), context: `净ROI ${reportedRatio(promotion.netRoi)}` },
            { label: '结算交易额', value: reportedMoney(promotion.settledGmv), context: `结算ROI ${reportedRatio(promotion.settledRoi)}` },
            { label: '净成交笔数', value: reportedInteger(promotion.netOrders), context: `结算 ${reportedInteger(promotion.settledOrders)} 笔` },
            { label: '曝光 / 点击', value: `${reportedInteger(promotion.impressions)} / ${reportedInteger(promotion.clicks)}`, context: promotion.bidding || '出价策略未标注' },
          ]} />
          <div className="product-master-summary">
            <div><span>推广组</span><strong>{promotion.group || '—'}</strong></div>
            <div><span>推广计划</span><strong>{promotion.promotionName || '—'}</strong></div>
            <div><span>出价方式</span><strong>{promotion.bidding || '—'}</strong></div>
            <div><span>点击率</span><strong>{promotion.impressions && promotion.clicks !== null ? `${(promotion.clicks / promotion.impressions * 100).toFixed(2)}%` : '—'}</strong></div>
          </div>
          <p className="methodology-note">当前源文件只有商品{queryWindowLabel}汇总，没有该商品的逐日序列，因此本页不生成伪趋势图。</p>
        </> : <div className="empty-state">该商品不在当前推广商品汇总中；未覆盖不等于花费或交易为零。</div>}
      </section>

      <section className="detail-section">
        <div className="section-heading-row"><div><h2>营销活动机会</h2><p>后台当前可见的活动权益、价格与30日销量</p></div></div>
        {opportunity ? <div className="product-master-summary">
          <div><span>30日销量</span><strong>{integer(opportunity.sales30d)}</strong></div>
          <div><span>当前价</span><strong>¥{opportunity.currentPrice}</strong></div>
          <div><span>活动价</span><strong>¥{opportunity.campaignPrice}</strong></div>
          <div><span>流量权益</span><strong>{opportunity.benefit || '—'}</strong></div>
        </div> : <div className="empty-state">该商品不在当前活动机会列表中；这不代表商品不可报名其他活动。</div>}
      </section>

      <section className="detail-section">
        <div className="section-heading-row"><div><h2>来源与口径</h2><p>仅列出本轮保留的非敏感聚合来源</p></div></div>
        <div className="internal-product-source">
          数据生成于 {formatTimestamp(data.generatedAt)} · 后台采集于 {formatTimestamp(data.meta.collectedAt)} · {data.meta.caliber}
        </div>
        <div className="table-responsive">
          <table className="source-table">
            <thead><tr><th>来源页面</th><th>原始文件</th><th>SHA256</th></tr></thead>
            <tbody>{data.sources.map((source) => <tr key={`${source.pageId}:${source.file}`}><td>{source.pageId}</td><td>{source.file}</td><td><code>{source.sha256}</code></td></tr>)}</tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
